import { makeStyles } from "@fluentui/react-components";
import { primer } from "./primerDarkTheme";

/* Grid column template — Name flexes, fixed widths for Status/Duration/Trend/LastRun/Video */
const GRID_COLS = "minmax(0,1fr) 76px 76px 96px 72px 96px";

export const useTestResultsTableStyles = makeStyles({
    root: {
        backgroundColor: primer.surface,
        borderRadius: "6px",
        border: `1px solid ${primer.border}`,
        overflow: "hidden",
    },
    toolbar: {
        display: "flex",
        gap: "8px",
        alignItems: "center",
        padding: "12px 16px",
        flexWrap: "wrap" as const,
    },
    searchBox: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        minWidth: "140px",
        flex: "1 1 140px",
        padding: "6px 10px",
        borderRadius: "6px",
        border: `1px solid ${primer.border}`,
        backgroundColor: primer.canvas,
    },
    searchIcon: { fontSize: "16px", color: primer.fgMuted, lineHeight: "1", display: "flex", alignItems: "center" },
    searchInput: {
        flex: "1",
        minWidth: "40px",
        border: "none",
        background: "none",
        outline: "none",
        color: primer.fg,
        fontSize: "13px",
    },
    pills: { display: "flex", gap: "6px", flexWrap: "wrap" as const },
    pill: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "4px 8px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 500,
        cursor: "pointer",
        border: `1px solid ${primer.border}`,
        color: primer.fgMuted,
        backgroundColor: "transparent",
        whiteSpace: "nowrap" as const,
    },
    pillActive: {
        borderTopColor: primer.blue,
        borderRightColor: primer.blue,
        borderBottomColor: primer.blue,
        borderLeftColor: primer.blue,
        backgroundColor: primer.surface,
        color: primer.fg,
    },
    divider: { height: "1px", backgroundColor: primer.border },
    dividerMuted: { height: "1px", backgroundColor: primer.borderMuted },

    /* Grid: header + rows share the same column template */
    grid: { display: "grid", gridTemplateColumns: GRID_COLS, alignItems: "center", padding: "0 16px", columnGap: "10px" },
    colHeader: { paddingTop: "8px", paddingBottom: "8px", backgroundColor: primer.canvas },
    colText: { fontSize: "11px", fontWeight: 600, color: primer.fgMuted, textTransform: "uppercase" as const, letterSpacing: "0.3px" },
    colSortable: { cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "3px", userSelect: "none" as const, "&:hover": { color: primer.fg } },

    groupHeader: {
        display: "flex",
        gap: "8px",
        alignItems: "center",
        padding: "8px 16px",
        backgroundColor: "rgba(255,255,255,0.03)",
        cursor: "pointer",
    },
    groupArrow: { fontSize: "8px", color: primer.fgMuted, fontWeight: 500 },
    groupName: { fontSize: "13px", fontWeight: 600, color: primer.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const },
    groupCount: {
        display: "inline-flex",
        alignItems: "center",
        height: "18px",
        padding: "0 6px",
        borderRadius: "10px",
        backgroundColor: "rgba(110,118,128,0.35)",
        fontSize: "11px",
        flexShrink: 0,
        fontWeight: 500,
        color: primer.fgMuted,
    },

    /* Row */
    row: { paddingTop: "8px", paddingBottom: "8px", borderBottom: `1px solid ${primer.borderMuted}` },
    rowFailed: { backgroundColor: "rgba(247,99,12,0.04)" },
    rowSkipped: { backgroundColor: "rgba(204,38,53,0.04)" },

    /* Cells: sized by grid; only local layout / typography here */
    nameCell: {
        display: "flex",
        alignItems: "center",
        gap: "6px",
        overflow: "hidden",
        minWidth: 0,
    },
    statusChar: { fontSize: "14px", fontWeight: 700, flexShrink: 0 },
    testName: { fontSize: "13px", color: primer.fg, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const },
    tag: {
        flexShrink: 0,
        borderRadius: "4px",
        padding: "2px 5px",
        fontSize: "10px",
        borderTopWidth: "1px",
        borderRightWidth: "1px",
        borderBottomWidth: "1px",
        borderLeftWidth: "1px",
        borderTopStyle: "solid",
        borderRightStyle: "solid",
        borderBottomStyle: "solid",
        borderLeftStyle: "solid",
    },
    statusCell: { overflow: "hidden", whiteSpace: "nowrap" as const },
    badge: {
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: "999px",
        fontSize: "11px",
        fontWeight: 500,
    },
    durCell: { fontSize: "12px", color: primer.fgMuted },
    trendCell: { display: "flex", alignItems: "center" },
    trendPct: { fontSize: "13px", color: primer.fgMuted },
    sparkline: { display: "flex", alignItems: "center", gap: "3px", height: "12px" },
    sparkBar: { width: "5px", height: "12px", borderRadius: "1px", flexShrink: 0 },
    noData: { fontSize: "10px", color: "rgba(122,130,140,0.6)" },
    lastRunCell: { fontSize: "11px", color: primer.fgMuted, whiteSpace: "nowrap" as const },
    playbackCell: {},
    watchBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "3px 8px",
        borderRadius: "4px",
        border: `1px solid ${primer.border}`,
        backgroundColor: "transparent",
        color: primer.fgMuted,
        fontSize: "11px",
        fontWeight: 500,
        cursor: "pointer",
        whiteSpace: "nowrap" as const,
    },
    watchBtnIcon: { fontSize: "10px" },
});
